import "./CustomMetricsModal.scss";
import { evaluate, random } from "mathjs";
import { Form, Modal } from "react-bootstrap";
import { KpiInfo } from "@blisspointmedia/bpm-types/dist/Kpis";
import { MdClose, MdSave } from "react-icons/md";
import { SingleChannelMediaTypes } from "../../Performance/performanceUtils";
import { useCompanyInfo } from "../../redux/company";
import * as R from "ramda";
import * as uuid from "uuid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_DECIMALS } from "@blisspointmedia/bpm-types/dist/formatUtils";
import {
  CustomMetric,
  CustomMetricKPI,
} from "@blisspointmedia/bpm-types/dist/pgTables/CustomMetric";
import {
  DuplicateMetricType,
  findDuplicateMetric,
} from "@blisspointmedia/bpm-types/dist/customMetricValidation";
import {
  CROSS_CHANNEL,
  SEARCHSHOPPING,
  YOUTUBE,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { Button, ButtonType } from "../../Components/Button/Button";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework/ButtonFramework";
import { Dropdown, DropdownToggleType } from "../../Components/Dropdown/Dropdown";
import {
  ColumnMetaData,
  ColumnMetaDataMap,
  convertKPIMapToOptions,
} from "../MetricsTable/metricsTableUtils";

interface CustomMetricsModalProps {
  columnMetaDataMap: ColumnMetaDataMap;
  customMetric: CustomMetric | undefined;
  disabled?: boolean;
  hidden?: boolean;
  kpiMetaData: Record<string, KpiInfo>;
  onHide: Function;
  prefix: SingleChannelMediaTypes | "cross_channel" | "pacing";
  save: Function;
}

const OPERATOR_LABEL_MAP = {
  "*": "x",
  "/": "÷",
  "^": "**",
};

export const OPERATORS = ["+", "-", "*", "/", "^"]; // Mathematical operators
export const FUNCTIONS = ["sqrt", "log", "abs"]; // Math functions (without parentheses)
const OPEN_PARENS = "(";
const CLOSE_PARENS = ")";

export const GLOBAL_KPI = "GLOBAL";

const testFormula = (formula: string, availableVariables: string[]) => {
  try {
    const fakeFetch = {};
    for (const dataVarName of availableVariables) {
      fakeFetch[dataVarName] = 1000 * random(0, 100);
    }
    evaluate(formula, { fetch: fakeFetch });
    return {};
  } catch (e) {
    return { message: (e as Error).message };
  }
};

const suggestNextTokens = (
  tokens: string[] | undefined,
  availableVariables: string[]
): Record<"availableVariables" | "functions" | "operators", string[] | undefined> => {
  const suggestedNextTokens: Record<
    "availableVariables" | "functions" | "operators",
    string[] | undefined
  > = {
    availableVariables: undefined,
    functions: undefined,
    operators: undefined,
  };
  if (R.isNil(tokens) || tokens.length === 0) {
    // Suggest variables, functions (always with '('), or an opening parenthesis
    return {
      ...suggestedNextTokens,
      availableVariables,
      functions: FUNCTIONS.map(fn => `${fn}(`),
      operators: [OPEN_PARENS],
    };
  }
  const lastToken = tokens[tokens.length - 1];
  // Count open vs close parentheses to detect unclosed function calls
  const openParensCount = tokens.filter(t => t === OPEN_PARENS).length;
  const closeParensCount = tokens.filter(t => t === CLOSE_PARENS).length;

  if (OPERATORS.includes(lastToken) || lastToken === OPEN_PARENS) {
    // After an operator or "(", suggest variables, functions (with '('), or "("
    return {
      ...suggestedNextTokens,
      availableVariables: lastToken === "^" ? undefined : availableVariables, // Prevent variables after exponent
      functions: FUNCTIONS.map(fn => `${fn}(`),
      operators: [OPEN_PARENS],
    };
  }

  if (availableVariables.includes(lastToken)) {
    // After a variable, suggest an operator or close parenthesis if needed
    suggestedNextTokens.operators = [...OPERATORS];
    if (openParensCount > closeParensCount) {
      suggestedNextTokens.operators.push(CLOSE_PARENS);
    }
    return suggestedNextTokens;
  }

  if (!isNaN(Number(lastToken))) {
    // Numbers should follow the same rules as variables
    suggestedNextTokens.operators = [...OPERATORS];
    if (openParensCount > closeParensCount) {
      suggestedNextTokens.operators.push(CLOSE_PARENS);
    }
    return suggestedNextTokens;
  }

  if (lastToken === CLOSE_PARENS) {
    // After ")", suggest an operator or another ")" if needed
    suggestedNextTokens.operators = [...OPERATORS];
    if (openParensCount > closeParensCount) {
      suggestedNextTokens.operators.push(CLOSE_PARENS);
    }
    return suggestedNextTokens;
  }

  if (FUNCTIONS.includes(lastToken.replace("(", ""))) {
    // Ensure function calls are properly closed
    return { ...suggestedNextTokens, operators: [OPEN_PARENS] };
  }

  return suggestedNextTokens; // Default case: No suggestions
};

const tokenizeFormula = (formula: string, allowedVariables: Record<string, string>): string[] => {
  const tokens: string[] = [];
  const tokenRegex = /(\d+\.\d+|\d+|[a-zA-Z_][a-zA-Z0-9_]*|[+\-*/^()])/g;
  let match;
  while ((match = tokenRegex.exec(formula)) !== null) {
    let token = match[0];
    // Check if the token is valid
    if (
      OPERATORS.includes(token) ||
      token === OPEN_PARENS ||
      token === CLOSE_PARENS ||
      !isNaN(token)
    ) {
      tokens.push(token); // Add numbers & operators directly
    } else if (FUNCTIONS.includes(token)) {
      tokens.push(token); // Add valid functions
    } else if (allowedVariables[token]) {
      tokens.push(token); // Add valid variables
    }
  }
  return tokens;
};

const CustomMetricsModal = ({
  columnMetaDataMap,
  customMetric,
  disabled = false,
  kpiMetaData,
  onHide,
  prefix,
  save,
}: CustomMetricsModalProps): JSX.Element => {
  const [compileError, setCompileError] = useState<string>();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [description, setDescription] = useState<string>();
  const [format, setFormat] = useState<string>();
  const [decimals, setDecimals] = useState<string>();
  const [formula, setFormulaRaw] = useState<string>();
  const [formulaKPIs, setFormulaKPIs] = useState<CustomMetricKPI[]>();
  const [displayName, setDisplayName] = useState<string>();
  const { cid } = useCompanyInfo();
  const descriptionAreaRefInput = useRef<HTMLTextAreaElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const stylizedPrefix =
    prefix === CROSS_CHANNEL
      ? "Cross Channel"
      : prefix === SEARCHSHOPPING
      ? "Search & Shopping"
      : prefix === YOUTUBE
      ? "YouTube"
      : `${prefix.charAt(0).toUpperCase()}${prefix.substring(1)}`;

  const availableMetricsMap = useMemo(() => {
    const availableMetricsMap: Record<string, string> = {};
    if (columnMetaDataMap) {
      for (const dataVarName of R.keys(columnMetaDataMap)) {
        const columnMetaData = columnMetaDataMap[dataVarName] as ColumnMetaData;
        if (R.isNil(columnMetaData.fetchGetter) && !columnMetaData.isCustom) {
          availableMetricsMap[dataVarName] = columnMetaData.displayName.replace("(000s)", "");
        }
      }
    }
    return availableMetricsMap;
  }, [columnMetaDataMap]);

  // Other companies' custom metrics on this channel, used to catch a formula that reinvents one
  // of them under a new name (see findDuplicateMetric below).
  const existingCustomMetrics = useMemo(
    () =>
      R.keys(columnMetaDataMap)
        .map(dataVarName => columnMetaDataMap[dataVarName as string]?.customMetaData)
        .filter((metric): metric is CustomMetric => Boolean(metric)),
    [columnMetaDataMap]
  );

  const updateCursorPosition = useCallback(() => {
    const pos = textAreaRef.current?.selectionStart;
    if (!R.isNil(pos)) {
      setCursorPosition(pos);
    }
  }, []);

  const currentFormulaTokens = useMemo(() => {
    if (formula) {
      const currentFormulaTokens = tokenizeFormula(formula, availableMetricsMap);
      return currentFormulaTokens;
    }
  }, [availableMetricsMap, formula]);

  const formulaToSave = useMemo(() => {
    let formulaToSave = "";
    if (currentFormulaTokens) {
      for (const token of currentFormulaTokens) {
        formulaToSave = `${formulaToSave}${
          R.isNil(columnMetaDataMap[token]) ? "" : "fetch."
        }${token}`;
      }
    }
    return formulaToSave;
  }, [columnMetaDataMap, currentFormulaTokens]);

  const suggestedNextTokens = useMemo(() => {
    const currentTokensBeforeCursor: string[] = [];
    let textIndex = 0;
    for (const token of R.defaultTo([], currentFormulaTokens)) {
      if (textIndex + token.length <= cursorPosition) {
        currentTokensBeforeCursor.push(token);
      }
      textIndex += token.length;
    }
    return suggestNextTokens(currentTokensBeforeCursor, R.keys(availableMetricsMap));
  }, [availableMetricsMap, currentFormulaTokens, cursorPosition]);

  const kpiOptions = useMemo(
    () => [
      {
        label: "Global KPI",
        value: GLOBAL_KPI,
      },
      ...convertKPIMapToOptions(prefix, kpiMetaData),
    ],
    [kpiMetaData, prefix]
  );

  const setFormula = useCallback(
    (text: string) => {
      setFormulaRaw(text);
      updateCursorPosition();
    },
    [updateCursorPosition, setFormulaRaw]
  );

  const forceCursorPosition = useCallback(
    (pos: number) => {
      if (textAreaRef.current) {
        textAreaRef.current.setSelectionRange(pos, pos);
        setCursorPosition(pos);
      }
    },
    [setCursorPosition]
  );

  const insertText = useCallback(
    (text: string) => {
      const formulaLength = R.sum(
        R.map(token => token.length, R.defaultTo([], currentFormulaTokens) as string[])
      );
      const selectionStart =
        textAreaRef &&
        textAreaRef.current &&
        !R.isNil(textAreaRef.current.selectionStart) &&
        document.activeElement === textAreaRef.current
          ? textAreaRef.current.selectionStart
          : R.defaultTo(formulaLength, cursorPosition);
      let textIndex = 0;
      let newFormula = "";
      let insertedText = false;
      for (const token of R.defaultTo([], currentFormulaTokens)) {
        if (textIndex + token.length > selectionStart && !insertedText) {
          newFormula = `${newFormula}${text}${token}`;
          textIndex += token.length;
          insertedText = true;
        } else {
          newFormula = `${newFormula}${token}`;
          textIndex += insertedText ? 0 : token.length;
        }
      }
      const newInsertedText = insertedText ? newFormula : `${newFormula}${text}`;
      const newCursorPosition = insertedText ? textIndex : `${newFormula}${text}`.length;
      setFormula(newInsertedText);
      forceCursorPosition(newCursorPosition);
    },
    [forceCursorPosition, setFormula, textAreaRef, currentFormulaTokens, cursorPosition]
  );

  const saveMetric = useCallback(() => {
    (async () => {
      const { message } = testFormula(formulaToSave, R.keys(availableMetricsMap));
      // `name` is the metric's PERMANENT id -- it is the `dataVarName` key stored in
      // metrics_table_presets (see V1_1785810577, which repoints presets by matching on it) and, going
      // forward, the id saved inside dashboarding ChartConfigs. Re-deriving it from the display name on
      // every save silently orphaned both. Derive it at CREATE time only; an edit renames
      // `display_name` -- the sole name field this modal exposes -- and leaves `name` frozen, so the two
      // may legitimately drift, exactly as they do in the static registry (`spend` / "Spend").
      const derivedName = R.toLower(R.defaultTo("", displayName).replaceAll(" ", "_"));
      const name = customMetric?.id ? R.defaultTo(derivedName, customMetric.name) : derivedName;
      if (message) {
        setCompileError(
          `There was an error with your metric: ${message}. Please update and retry saving.`
        );
        return;
      }
      // Validate what the user actually typed, not the frozen id -- on an edit `name` is populated
      // from the existing record and would mask a blank Metric Name field.
      if (!derivedName) {
        setCompileError("A name is required for a custom metric");
        return;
      }
      if (
        R.filter(
          dataVarName => R.isNil(customMetric) || dataVarName !== customMetric.name,
          R.keys(columnMetaDataMap) as string[]
        ).includes(name)
      ) {
        setCompileError(
          `${name} is already used as a ${
            columnMetaDataMap[name] ? "default" : ""
          } metric, please rename`
        );
        return;
      }
      if (formulaToSave.length === 0) {
        setCompileError("A formula is required for a custom metric");
        return;
      }
      const duplicateMetric = findDuplicateMetric(
        formulaToSave,
        prefix,
        [cid],
        R.isNil(customMetric)
          ? existingCustomMetrics
          : existingCustomMetrics.filter(metric => metric.id !== customMetric.id),
        R.defaultTo([], formulaKPIs)
      );
      if (duplicateMetric) {
        setCompileError(
          duplicateMetric.type === DuplicateMetricType.STANDARD
            ? `This formula is the same as the standard "${duplicateMetric.prettyName}" metric -- use that instead of creating a custom metric.`
            : `This formula is the same as the existing custom metric "${duplicateMetric.prettyName}".`
        );
        return;
      }
      let isFunction = false;
      for (const symbol of [...OPERATORS, ...FUNCTIONS]) {
        if (formulaToSave.includes(symbol)) {
          isFunction = true;
          break;
        }
      }
      if (!isFunction) {
        setCompileError(`${formulaToSave.replaceAll("fetch.", "")} already exists.`);
        return;
      }
      setCompileError(undefined);
      const newCustomMetric = {
        ...R.defaultTo({}, customMetric),
        name,
        companies: [cid],
        channel: prefix,
        decimals: Number(decimals ?? 0),
        description,
        display_name: displayName,
        function_string: formulaToSave,
        formatter: format,
        min_is_best: false,
        required_columns: [],
        kpis: R.defaultTo([], formulaKPIs),
      } as Partial<CustomMetric>;
      for (const token of R.defaultTo([], currentFormulaTokens)) {
        if (availableMetricsMap[token]) {
          newCustomMetric.required_columns = R.uniq([
            ...(newCustomMetric.required_columns as string[]),
            token,
          ]);
        }
      }
      await save(newCustomMetric);
    })();
  }, [
    availableMetricsMap,
    cid,
    columnMetaDataMap,
    currentFormulaTokens,
    customMetric,
    decimals,
    description,
    displayName,
    existingCustomMetrics,
    format,
    formulaKPIs,
    formulaToSave,
    prefix,
    save,
  ]);

  const onDescriptionInput = useCallback(() => {
    if (descriptionAreaRefInput.current) {
      descriptionAreaRefInput.current.style.height = "auto"; // Reset height
      const minHeight =
        descriptionAreaRefInput.current.scrollHeight > descriptionAreaRefInput.current.clientHeight
          ? `${descriptionAreaRefInput.current.scrollHeight}px`
          : `${descriptionAreaRefInput.current.clientHeight}px`; // Ensures 1 row minimum height
      descriptionAreaRefInput.current.style.height = minHeight;
    }
  }, [descriptionAreaRefInput]);

  useEffect(() => {
    if (customMetric) {
      if (R.isNil(formula)) {
        setFormula(customMetric.function_string.replace(/fetch./g, ""));
      }
      if (R.isNil(description)) {
        setDescription(customMetric.description);
      }
      if (R.isNil(format)) {
        setFormat(customMetric.formatter);
      }
      if (R.isNil(decimals)) {
        setDecimals(`${customMetric.decimals}`);
      }
      if (R.isNil(displayName)) {
        setDisplayName(customMetric.display_name);
      }
      if (R.isNil(formulaKPIs)) {
        setFormulaKPIs(customMetric.kpis);
      }
    } else if (R.isNil(format)) {
      // Since this is the only default we need to set, can't use a separate else block
      setFormat("number");
    }
  }, [customMetric, decimals, description, displayName, format, formula, formulaKPIs, setFormula]);

  useEffect(() => {
    const kpiTokens = R.filter(
      token => token.includes("volume") || token.includes("revenue"),
      R.defaultTo([], currentFormulaTokens) as string[]
    );
    if (R.isNil(customMetric) && R.isNil(formulaKPIs)) {
      setFormulaKPIs([]);
    } else if (formulaKPIs && formulaKPIs.length !== kpiTokens.length) {
      const newFormulaKPIs: CustomMetricKPI[] = [];
      for (let i = 0; i < kpiTokens.length; ++i) {
        newFormulaKPIs.push(
          R.defaultTo(
            { dataVarName: kpiTokens[i], kpi: GLOBAL_KPI },
            formulaKPIs[i]
          ) as CustomMetricKPI
        );
      }
      setFormulaKPIs(newFormulaKPIs);
    }
  }, [currentFormulaTokens, customMetric, formulaKPIs, kpiOptions]);

  const [suggestedOperators, suggestedVariables] = useMemo(() => {
    const suggestedOperators = R.map((suggestion: string) => {
      const suggestionFormatted = R.defaultTo(suggestion, OPERATOR_LABEL_MAP[suggestion]);
      const suggestions = [
        ...R.defaultTo([], suggestedNextTokens.operators),
        ...R.defaultTo([], suggestedNextTokens.functions),
      ];
      return (
        <Button
          background="light"
          className="suggestion"
          design="secondary"
          disabled={!suggestions.includes(suggestion) && !suggestions.includes(`${suggestion}(`)}
          key={uuid.v4()}
          onClick={() => insertText(suggestion)}
          size="sm"
          type={ButtonType.FILLED}
        >
          {suggestionFormatted}
        </Button>
      );
    }, R.uniq([OPEN_PARENS, CLOSE_PARENS, ...OPERATORS, ...FUNCTIONS]));
    const suggestedVariables = R.map((suggestion: string) => {
      const suggestionFormatted = R.defaultTo(suggestion, availableMetricsMap[suggestion]);
      return (
        <Button
          background="light"
          className="suggestion"
          design="secondary"
          disabled={!suggestedNextTokens.availableVariables?.includes(suggestion)}
          key={uuid.v4()}
          onClick={() => insertText(suggestion)}
          size="sm"
          type={ButtonType.FILLED}
        >
          {suggestionFormatted}
        </Button>
      );
    }, R.defaultTo([], R.keys(availableMetricsMap)));
    return [suggestedOperators, suggestedVariables];
  }, [availableMetricsMap, insertText, suggestedNextTokens]);

  const formulaWithKPIs = useMemo(() => {
    let formulaWithKPIs = "";
    let kpiIndex = 0;
    for (const token of R.defaultTo([], currentFormulaTokens)) {
      formulaWithKPIs = `${formulaWithKPIs} ${
        (token.includes("volume") || token.includes("revenue")) &&
        formulaKPIs &&
        formulaKPIs[kpiIndex]
          ? `${token.includes("revenue") ? "revenue (" : ""}${formulaKPIs[kpiIndex].kpi}${
              token.includes("revenue") ? ")" : ""
            }`
          : token
      }`;
      if (
        formulaKPIs &&
        formulaKPIs[kpiIndex] &&
        (token.includes("volume") || token.includes("revenue"))
      ) {
        kpiIndex++;
      }
    }
    return formulaWithKPIs;
  }, [currentFormulaTokens, formulaKPIs]);

  const modalHeader = (
    <Modal.Header>
      <div className="titleContainer">
        <Modal.Title>{customMetric ? "Edit" : "New"} Custom Metric:</Modal.Title>
        <div className="channelLabel"> {stylizedPrefix}</div>
      </div>
      <div className="closeContainer" onClick={() => onHide()}>
        <MdClose />
      </div>
    </Modal.Header>
  );
  const modalBody = (
    <Modal.Body>
      <div className="metricSettingsContainer">
        <div className="name">
          <div className="label">Metric Name*</div>
          <Form.Control
            onChange={e => setDisplayName(R.defaultTo("", e.target.value).slice(0, 20))}
            placeholder={"Ex: ACOS"}
            value={displayName}
          />
        </div>
        <div className="description">
          <div className="label">Description</div>
          <Form.Control
            as="textarea"
            onChange={e => setDescription(R.defaultTo("", e.target.value).slice(0, 200))}
            onInput={onDescriptionInput}
            placeholder={"Ex: Amazon Cost of Sales"}
            ref={descriptionAreaRefInput}
            rows={1}
            style={{ resize: "none", overflow: "hidden" }}
            value={description}
          />
        </div>
        <div className="format">
          <div className="label">Format</div>
          <Dropdown
            options={[
              { label: "Number (123)", value: "number" },
              { label: "Percent (12.3%)", value: "percent" },
              { label: "Money ($123)", value: "spend" },
            ]}
            onChange={val => setFormat(val)}
            size="lg"
            type={DropdownToggleType.OUTLINED}
            value={format}
          />
        </div>
        <div className="decimals">
          <div className="label">Decimals</div>
          <Form.Control
            max={MAX_DECIMALS}
            min={0}
            onChange={e => {
              // Only allow empty or a single digit from 0 to MAX_DECIMALS
              const { value } = e.target;
              if (value === "" || (/^\d$/.test(value) && Number(value) <= MAX_DECIMALS)) {
                setDecimals(value);
              }
            }}
            step={1}
            type="number"
            value={R.defaultTo("", decimals)}
          />
        </div>
      </div>
      <div className="formulaContainer">
        <Form.Control
          as="textarea"
          className="formulaTextBox"
          onChange={e => {
            setFormula(e.currentTarget.value);
            setCompileError(undefined);
          }}
          onClick={updateCursorPosition}
          onFocus={updateCursorPosition}
          onKeyUp={updateCursorPosition}
          placeholder={
            "Add metrics and functions using the tokens below. Add numerical values using your keyboard. "
          }
          ref={textAreaRef}
          rows={6}
          spellCheck={false}
          value={formula}
        />
        <div className="suggestionLists">
          <div className="suggestionList">{suggestedOperators}</div>
          <div className="divider"></div>
          <div className="suggestionList">{suggestedVariables}</div>
        </div>
        <>
          <div className="formulaKPIMapping">
            {R.defaultTo([], formulaKPIs).map((kpiInfo, i) => (
              <div className="kpiSelector">
                <div className="label">KPI {i + 1}</div>
                <Dropdown
                  onChange={kpi =>
                    setFormulaKPIs(
                      R.adjust(
                        i,
                        oldKPIInfo => ({ ...oldKPIInfo, kpi }),
                        R.defaultTo([], formulaKPIs)
                      )
                    )
                  }
                  options={kpiOptions}
                  placeholder="Select a KPI"
                  type={DropdownToggleType.OUTLINED}
                  value={kpiInfo.kpi}
                />
              </div>
            ))}
          </div>
          <div className="kpiFormula">{formulaWithKPIs}</div>
        </>
        <div className="calcsMessage">
          Calculated metrics (i.e. ROAS, CPX) must be entered as their equations.
        </div>
        {compileError && <div className="compileError">{compileError}</div>}
      </div>
    </Modal.Body>
  );

  const modalFooter = (
    <Modal.Footer>
      <Button
        className="cancelButton"
        design="secondary"
        onClick={() => onHide()}
        size={"sm"}
        type={ButtonType.OUTLINED}
      >
        Cancel
      </Button>
      <Button
        className="saveAndApplyButton"
        onClick={() => saveMetric()}
        size={"sm"}
        type={ButtonType.FILLED}
        icon={<MdSave />}
        variant={ButtonFrameworkVariant.LEADING_ICON}
      >
        Save
      </Button>
    </Modal.Footer>
  );

  return (
    <Modal
      centered
      className={`customMetricsModal ${disabled ? "disabled" : ""}`}
      onHide={() => onHide()}
      show
      size="lg"
      aria-hidden="false"
    >
      {modalHeader}
      {modalBody}
      {modalFooter}
    </Modal>
  );
};

export default CustomMetricsModal;
